"use client";
import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

interface ContactSectionProps {
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
}

export default function ContactSection({ isVisible, onVisibilityChange }: ContactSectionProps) {
  const sectionRef = useRef(null);
  const t = useTranslations();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisibilityChange(true);
        }
      },
      {
        threshold: 0.02,
        rootMargin: '0px 0px 50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [onVisibilityChange]);

  return (
    <>
      {/* Contact Section Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        .contact-bg-1 {
          filter: blur(170px);
        }
        
        @media (max-width: 768px) {
          .contact-title {
            font-size: 32px !important;
          }
          
          .contact-description {
            font-size: 18px !important;
          }
          
          .section-padding {
            padding: 60px 16px !important;
          }
          
          .contact-bg-1 {
            filter: none !important;
            border-radius: 50% !important;
            transform: translate(-50%, -50%) rotate(15deg) !important;
            width: 600px !important;
            height: 250px !important;
            background: radial-gradient(ellipse at center, 
              rgba(167, 2, 8, 0.5) 0%, 
              rgba(167, 2, 8, 0.35) 12%, 
              rgba(167, 2, 8, 0.25) 25%, 
              rgba(167, 2, 8, 0.18) 35%, 
              rgba(167, 2, 8, 0.12) 45%, 
              rgba(167, 2, 8, 0.08) 55%, 
              rgba(167, 2, 8, 0.05) 65%, 
              rgba(167, 2, 8, 0.03) 75%, 
              rgba(167, 2, 8, 0.015) 85%, 
              transparent 100%) !important;
            top: 40% !important;
            left: 50% !important;
          }
        }

        @media (max-width: 480px) {
          .contact-title {
            font-size: 28px !important;
          }
          
          .contact-description {
            font-size: 16px !important;
          }
          
          .section-padding {
            padding: 50px 12px !important;
          }
        }
      `}} />

      <section 
        ref={sectionRef} 
        style={{
          background: "#000",
          padding: "80px 24px",
          position: "relative",
          overflow: "hidden",
        }}
        className="section-padding"
      >
        {/* Black Square at top center */}
        <div style={{
          position: "absolute",
          top: "-30px",
          left: "50%",
          transform: "translateX(-50%) rotate(45deg)",
          width: "60px",
          height: "60px",
          background: "#000",
          zIndex: 2,
        }}></div>

        {/* Background Rectangle with blur */}
        <div 
          className="contact-bg-1"
          style={{
            position: "absolute",
            top: "30%",
            left: "40%",
            transform: "translate(-50%, -50%) rotate(25deg)",
            width: "1200px",
            height: "200px",
            background: "linear-gradient(135deg,rgb(167, 2, 8) 100%, rgba(140, 7, 25, 0.3) 100%)",
            borderRadius: "10px",
            zIndex: 0,
            pointerEvents: "none",
            opacity: isVisible ? 1 : 0,
            transition: "opacity 2s ease-out",
            transitionDelay: "0.1s",
          }}
        ></div>

        <div style={{
          maxWidth: "800px",
          margin: "0 auto",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}>
          {/* Main Text */}
          <h2 style={{
            fontSize: "48px",
            fontWeight: 600,
            color: "#fff",
            lineHeight: 1.2,
            marginBottom: "30px",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.1s",
          }}
          className="contact-title">
            {t('oldFurnitureRemoval.contact.title')}
          </h2>

          <p style={{
            fontSize: "18px",
            color: "rgba(255,255,255,0.85)",
            lineHeight: 1.6,
            marginBottom: "20px",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.15s",
          }}>
            {t('oldFurnitureRemoval.contact.subtitle')}
          </p>

          <p style={{
            fontSize: "24px",
            color: "rgba(255,255,255,0.9)",
            lineHeight: 1.4,
            marginBottom: "50px",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.2s",
          }}
          className="contact-description">
            {t('oldFurnitureRemoval.contact.description')}
          </p>

          {/* Contact Us Button */}
          <a href="#" style={{
            display: "inline-flex",
            alignItems: "center",
            background: "rgba(50, 50, 50, 0.5)",
            color: "#fff",
            padding: "12px 18px",
            fontSize: "12px",
            fontWeight: 400,
            borderRadius: "9px",
            textDecoration: "none",
            textTransform: "uppercase",
            gap: "12px",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.3s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 8px 25px rgba(50, 50, 50, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0px)";
            e.currentTarget.style.boxShadow = "none";
          }}
          >
            {t('oldFurnitureRemoval.contact.button')}
            <div style={{
              width: "32px",
              height: "32px",
              background: "#B51F24",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "20px",
              fontWeight: "bold",
            }}>
              <Image 
                src="/images/components/arrow.png" 
                alt="Arrow" 
                width={7}
                height={7}
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </div>
          </a>
        </div>
      </section>
    </>
  );
} 